import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import type { Actor } from '../lib/events'
import { initials, relativeTime } from '../lib/format'
import { colorVar, type LabelDef } from '../lib/model'
import { Icon, type IconName } from './Icon'
import { Panel, useDismiss } from './Picker'

/* Faces — an initial avatar, an agent's mark, the workspace's own — run on their
   own three-step scale, because they are objects rather than glyphs and a 19px one
   next to a 21px one is just a wobble:

     16  inline, in a line of `.meta`
     20  in a row: the default
     24  a header or a footer, where the mark stands alone

   They were 16, 18, 19, 20, 21, 22, 24, 28 and 30. */

/** Per-person hue on initial avatars — the cheapest warmth in the app. */
const AVATAR_HUES = [18, 196, 154, 272, 340, 42, 218, 96]

function hueFor(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return AVATAR_HUES[Math.abs(h) % AVATAR_HUES.length]!
}

export function Avatar({ actor, size = 20 }: { actor: Actor | null; size?: number }) {
  const name = actor?.name ?? '?'
  const hue = hueFor(actor?.id ?? name)
  return (
    <span
      title={name}
      style={{
        width: size,
        height: size,
        background: `hsl(${hue} 38% 45% / 0.16)`,
        color: `hsl(${hue} 44% 38%)`,
        fontSize: size * 0.4,
        fontFamily: 'var(--mono)',
      }}
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold"
    >
      {initials(name)}
    </span>
  )
}

/** The marker for an entry an ingest agent wrote rather than a human. */
export function AgentAvatar({ size = 20 }: { size?: number }) {
  return (
    <span
      title="Written by an agent"
      style={{
        width: size,
        height: size,
        background: 'color-mix(in srgb, var(--brass) 16%, transparent)',
        color: 'var(--brass)',
      }}
      className="inline-flex shrink-0 items-center justify-center rounded-full"
    >
      <Icon name="Bot" size={size * 0.6} />
    </span>
  )
}

export function ActorStamp({
  actor,
  ts,
  prefix = 'by',
}: {
  actor: Actor | null
  ts: string
  prefix?: string
}) {
  return (
    <span className="meta inline-flex items-center gap-1.5">
      <Avatar actor={actor} size={16} />
      <span>
        {prefix} {actor?.name ?? 'unknown'} · {relativeTime(ts)}
      </span>
    </span>
  )
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon: IconName
  title: string
  hint?: string | undefined
  action?: ReactNode | undefined
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-full"
        style={{ background: 'var(--hover)', color: 'var(--faint)' }}
      >
        <Icon name={icon} size={20} />
      </div>
      <div className="text-lead font-medium">{title}</div>
      {hint && (
        <div className="text-body max-w-sm" style={{ color: 'var(--muted)' }}>
          {hint}
        </div>
      )}
      {action}
    </div>
  )
}

/**
 * The button. Three variants, two heights, and nothing else.
 *
 * There were seven treatments on the record page alone — a filled New, a bordered
 * Add artifact, a bare "attach a deal", a smaller bordered Upload, a filled Log it,
 * an underlined mono "add item" and the dashed `.adder` — because 162 buttons in
 * this app were written as raw `<button>` and 14 went through here. The variants
 * below cover every one of those jobs:
 *
 *   primary  the one committing action on a screen. Filled, in the workspace's
 *            own accent, so it is also the only place brand appears on a control.
 *   work     the same job on a screen that belongs to work rather than to money:
 *            filled, in marine. New project, approve the batch, mint a seat.
 *   default  everything else. A hairline on paper.
 *   quiet    a row-level action: restore, cancel, "add one". Text, no box.
 *
 * `size="dense"` is the 28px row height; the default 32px is the height of a bar.
 * Heights are set rather than left to padding so a row of buttons lines up even
 * when one of them carries an icon and the others do not.
 *
 * The one deliberate exception in the app is `.adder` in global.css: a dashed
 * outline means "this row does not exist yet" and nothing else here says that.
 */
export function Button({
  children,
  onClick,
  variant = 'default',
  size = 'default',
  type = 'button',
  title,
  disabled,
  className = '',
  'aria-label': ariaLabel,
}: {
  children: ReactNode
  onClick?: (() => void) | undefined
  variant?: 'default' | 'primary' | 'work' | 'quiet'
  size?: 'default' | 'dense'
  type?: 'button' | 'submit'
  title?: string | undefined
  disabled?: boolean | undefined
  className?: string
  'aria-label'?: string | undefined
}) {
  const inline: CSSProperties =
    variant === 'primary'
      ? { background: 'var(--accent)', color: 'var(--accent-contrast)' }
      : variant === 'work'
        ? // Filled, like `primary`, but in work's own hue: the button that makes a
          // new project or approves an agent's batch is the committing action on
          // its screen AND belongs to the work half of the palette. `--panel`
          // reads on marine in both themes, because both move together.
          { background: 'var(--marine)', color: 'var(--panel)' }
        : variant === 'default'
          ? { borderColor: 'var(--border)', background: 'var(--panel)', borderWidth: 1 }
          : { color: 'var(--muted)' }

  const box =
    variant === 'quiet'
      ? size === 'dense'
        ? 'h-7 px-1.5'
        : 'h-8 px-2'
      : size === 'dense'
        ? 'h-7 px-2.5'
        : 'h-8 px-3'

  return (
    <button
      type={type}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      disabled={disabled}
      className={`rounded-control text-body inline-flex shrink-0 items-center justify-center gap-1.5 font-medium whitespace-nowrap disabled:opacity-50 ${box} ${
        variant === 'quiet' ? 'hover:text-[var(--fg)]' : ''
      } ${className}`}
      style={inline}
    >
      {children}
    </button>
  )
}

/* `PageHead` stood here: a second page-title block, at 23px against the 21px of the
   one in Page.tsx, reachable from nothing. The page header the app actually draws is
   `PageHeader` in components/Page.tsx, and it is the only one. */

/** Hairline section header. Sections are separated by air and a rule, not boxes. */
export function SectionHead({
  title,
  note,
  right,
}: {
  title: string
  note?: string | undefined
  right?: ReactNode
}) {
  return (
    <div className="sechead mb-2">
      <h2 className="text-body font-semibold">{title}</h2>
      {note && (
        <span
          className="min-w-0"
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 'var(--text-label)',
            color: 'var(--faint)',
          }}
        >
          {note}
        </span>
      )}
      {right && <span className="ml-auto">{right}</span>}
    </div>
  )
}

export function Section({
  title,
  note,
  right,
  children,
  /**
   * Indent the body under its heading — on by default. Flush to the same left edge, a
   * heading looked like a sibling of the rows above it rather than the parent of the
   * ones below; the indent does the parsing work.
   *
   * **Nothing passes false.** Fifteen callers did, which made the client pages read as
   * a flat stack of rules while the detail pages read as an outline, and there was no
   * reason behind which page got which. The exception the escape hatch was written for
   * — a body whose columns must line up with something OUTSIDE the section — has never
   * actually occurred: `.rows` is a stack with separators, and each row lays itself
   * out, so indenting a section moves the whole block and internal alignment survives.
   * If you reach for `indent={false}`, check that the body really is such a grid; the
   * board's subgrid is the only shape in the app that would qualify, and it does not
   * live in a Section.
   */
  indent = true,
}: {
  title: string
  note?: string | undefined
  right?: ReactNode
  children: ReactNode
  indent?: boolean
}) {
  return (
    <section className="mb-8">
      <SectionHead title={title} note={note} right={right} />
      {indent ? <div className="pl-3.5 sm:pl-5">{children}</div> : children}
    </section>
  )
}

/** A colored pill. `def` comes straight from a model vocabulary so the color and
 *  the label are one decision. */
export function Pill({
  def,
  label,
  tick = true,
  onClick,
  title,
  after,
}: {
  def?: LabelDef | { color: string } | undefined
  label: string
  tick?: boolean
  onClick?: () => void
  title?: string
  /** trailing slot — the chevron on a pill that opens a menu, and nothing else */
  after?: ReactNode
}) {
  const style = { '--pc': `var(${colorVar(def?.color)})` } as CSSProperties
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        className="pill pill-btn max-w-full"
        style={style}
      >
        {tick && <span className="tick" />}
        <span className="truncate">{label}</span>
        {after}
      </button>
    )
  }
  return (
    <span className="pill" style={style} title={title}>
      {tick && <span className="tick" />}
      {label}
      {after}
    </span>
  )
}

/**
 * "N archived", which toggles them into view.
 *
 * Every list in crum that can archive something shows this, in the same place and
 * the same shape, because archiving is only half a verb: a thing you can put away
 * and not get back is a thing you deleted. It disappears at zero — the count is
 * the only reason to look.
 */
export function ArchivedToggle({
  count,
  on,
  onToggle,
}: {
  count: number
  on: boolean
  onToggle: () => void
}) {
  if (count === 0) return null
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      title={on ? `Hide the ${count} archived` : `Show the ${count} archived`}
      className="rounded-control flex items-center gap-1.5 px-2 py-1"
      style={{
        fontFamily: 'var(--mono)',
        fontSize: 'var(--text-micro)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: on ? 'var(--accent)' : 'var(--faint)',
      }}
    >
      <Icon name="Archive" size={12} /> {count}
    </button>
  )
}

/** One archived thing, hatched, with the way back. */
export function ArchivedRow({
  children,
  onRestore,
}: {
  children: React.ReactNode
  onRestore: () => void
}) {
  return (
    <div className="hatched flex w-full items-center gap-3 py-2">
      <span className="text-body min-w-0 flex-1 truncate" style={{ color: 'var(--muted)' }}>
        {children}
      </span>
      <button
        type="button"
        onClick={onRestore}
        className="text-label shrink-0 font-medium"
        style={{ color: 'var(--accent)' }}
      >
        Restore
      </button>
    </div>
  )
}

/**
 * Inline confirm for a destructive action. Deliberately NOT window.confirm: the
 * gt app iframe has no `allow-modals`, so confirm/prompt/alert silently no-op and
 * the action would appear to do nothing at all.
 */
export function ConfirmAction({
  icon = 'Archive',
  title,
  confirmLabel = 'Confirm',
  onConfirm,
}: {
  icon?: IconName
  title: string
  confirmLabel?: string
  onConfirm: () => void
}) {
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    if (!armed) return
    const t = setTimeout(() => setArmed(false), 4000)
    return () => clearTimeout(t)
  }, [armed])

  if (armed) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <button
          type="button"
          onClick={onConfirm}
          className="rounded-control text-label px-2 py-1 font-medium"
          style={{
            background: 'color-mix(in srgb, var(--crit) 14%, transparent)',
            color: 'var(--crit)',
          }}
        >
          {confirmLabel}
        </button>
        <button
          type="button"
          onClick={() => setArmed(false)}
          className="text-label"
          style={{ color: 'var(--muted)' }}
        >
          Cancel
        </button>
      </span>
    )
  }
  return (
    <button
      type="button"
      title={title}
      onClick={() => setArmed(true)}
      className="opacity-45 hover:opacity-100"
      aria-label={title}
    >
      <Icon name={icon} size={14} />
    </button>
  )
}

/** Click-to-edit text, committed on blur or Enter. */
export function EditableText({
  value,
  placeholder,
  type = 'text',
  className = '',
  style,
  format,
  onSave,
}: {
  value: string
  placeholder?: string | undefined
  type?: 'text' | 'email' | 'url' | 'number' | 'date'
  className?: string
  style?: CSSProperties
  /** how the committed value reads when idle — money wants "$48,000", the input wants 48000 */
  format?: (v: string) => string
  onSave: (v: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [v, setV] = useState(value)
  useEffect(() => setV(value), [value])

  const commit = () => {
    if (v !== value) onSave(v)
    setEditing(false)
  }
  if (editing) {
    return (
      <input
        autoFocus
        type={type === 'number' ? 'text' : type}
        value={v}
        inputMode={type === 'number' ? 'decimal' : undefined}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') {
            setV(value)
            setEditing(false)
          }
        }}
        placeholder={placeholder}
        className={`rounded-control border px-2 py-1 outline-none ${className}`}
        style={{ borderColor: 'var(--border)', background: 'var(--panel)', ...style }}
      />
    )
  }
  const display = value.trim() && format ? format(value.trim()) : value.trim()
  return (
    <span
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') setEditing(true)
      }}
      className={`rounded-control inline-block cursor-text px-2 py-1 hover:bg-[var(--hover)] ${className}`}
      style={{ ...(display ? {} : { color: 'var(--faint)' }), ...style }}
    >
      {display || placeholder || 'Add…'}
    </span>
  )
}

/**
 * A dropdown over a model vocabulary, rendered as the pill it sets.
 *
 * This is the app's ONE menu-shaped-as-its-own-value, and it is what the board's
 * Status column became: a set status and a derived `Blocked` are now the same pill
 * at the same height, where before one was a native `<select>` and the other a
 * bordered chip of a different width. The dismissal and the panel come from
 * ./Picker, so this and the text menu and the date cell cannot drift apart.
 */
export function PillSelect({
  options,
  value,
  placeholder = 'set',
  onPick,
}: {
  options: LabelDef[]
  value: string
  placeholder?: string
  onPick: (key: string) => void
}) {
  const [open, setOpen] = useState(false)
  const wrap = useDismiss(open, () => setOpen(false))
  const current = options.find((o) => o.key === value)

  return (
    <span ref={wrap} className="relative inline-flex min-w-0 justify-self-start">
      <Pill
        def={current ?? { color: 'grey' }}
        label={current?.label ?? placeholder}
        onClick={() => setOpen((v) => !v)}
        after={<Icon name="ChevronDown" size={12} className="-mr-1 opacity-60" />}
      />
      {open && (
        <Panel>
          <ul role="listbox">
            {options.map((o) => (
              <li key={o.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.key === value}
                  onClick={() => {
                    onPick(o.key)
                    setOpen(false)
                  }}
                  className="text-body flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[var(--hover)]"
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: `var(${colorVar(o.color)})` }}
                  />
                  <span className="truncate">{o.label}</span>
                  {o.key === value && (
                    <Icon
                      name="Check"
                      size={12}
                      className="ml-auto shrink-0"
                      style={{ color: 'var(--muted)' }}
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </span>
  )
}

/**
 * The stat strip — the one card-shaped thing on a page, and only because a row of
 * derived numbers genuinely is an object.
 *
 * **There were two of these**: this one, used by Overview, and `FactStrip` in
 * Page.tsx, which had a sub-line and linkable cells and no callers at all. They
 * differed in every measurement — 20px against 19px, a `.card` border against a
 * 1px-gap grid, `px-4 py-3` against `px-3 py-2` — and the Deals page, needing a
 * sub-line, hand-rolled a third rather than using either. This is the union of
 * the two; the other is gone.
 *
 * It renders at zero on purpose. A brand new client has no money, no milestone and
 * no open work, and showing that is still an answer: hiding a fact with nothing to
 * say is what made a quiet engagement read as an empty screen rather than a calm
 * one. So there are no conditional facts here.
 */
export interface Stat {
  label: string
  value: string
  /** one short line under the number: the count, the name, what it refers to */
  sub?: string | undefined
  /** a token NAME (`--ember`), for the value only, never the label */
  hue?: string | undefined
  /** makes the cell a link to the list the number came from */
  to?: string | undefined
  /** replaces the number outright, for a cell that draws rather than counts */
  extra?: ReactNode
}

export function Stats({ items }: { items: Stat[] }) {
  const cell = (s: Stat) => (
    <>
      <span className="eyebrow">{s.label}</span>
      {s.extra ?? (
        <span
          className="num text-title mt-0.5 block truncate leading-tight tracking-tight"
          style={{ color: s.hue ? `var(${s.hue})` : undefined }}
        >
          {s.value}
        </span>
      )}
      {s.sub !== undefined && (
        <span className="meta block truncate" title={s.sub || undefined}>
          {s.sub || ' '}
        </span>
      )}
    </>
  )
  return (
    <div className="card mb-8 flex flex-wrap overflow-hidden">
      {items.map((s, i) => {
        const edge = {
          borderRight: i < items.length - 1 ? '1px solid var(--rule-soft)' : undefined,
        }
        const box = 'flex min-w-[132px] flex-1 flex-col px-4 py-3'
        return s.to ? (
          <NavLink key={s.label} to={s.to} className={box} style={edge}>
            {cell(s)}
          </NavLink>
        ) : (
          <div key={s.label} className={box} style={edge}>
            {cell(s)}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Contact over the last 12 weeks, drawn from entry timestamps. Nothing extra is
 * stored to make this work — it is the log, read sideways.
 */
export function Sparkline({
  values,
  width = 96,
  height = 24,
  hue = 'var(--ember)',
}: {
  values: number[]
  width?: number
  height?: number
  hue?: string
}) {
  if (values.length < 2) return null
  const max = Math.max(1, ...values)
  const step = width / (values.length - 1)
  const pts = values.map((v, i) => [i * step, height - 2 - (v / max) * (height - 6)] as const)
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
  const last = pts[pts.length - 1]!
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Contact over the last 12 weeks"
    >
      <path
        d={`${line} L${width} ${height} L0 ${height} Z`}
        fill={`color-mix(in srgb, ${hue} 14%, transparent)`}
      />
      <path d={line} fill="none" stroke={hue} strokeWidth={1.3} strokeLinejoin="round" />
      <circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r={2.2} fill={hue} />
    </svg>
  )
}
